import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { formatCost, worksiteCostLow, worksiteCostHigh } from '../../lib/capex-utils'
import type { CapexPlan, Worksite } from './types'

interface SubmitModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  plan: CapexPlan
  worksites: Worksite[]
  onConfirm: (note: string) => void
}

export function SubmitModal({
  open,
  onOpenChange,
  plan,
  worksites,
  onConfirm,
}: SubmitModalProps) {
  const { t } = useTranslation('capex')
  const [note, setNote] = useState('')

  const nonIgnored = worksites.filter(ws => ws.status !== 'ignored')
  const draftCount = worksites.filter(ws => ws.status === 'draft').length
  const totalLow = nonIgnored.reduce((sum, ws) => sum + worksiteCostLow(ws), 0)
  const totalHigh = nonIgnored.reduce((sum, ws) => sum + worksiteCostHigh(ws), 0)
  const envelopeDisplay =
    totalLow === totalHigh
      ? formatCost(totalLow)
      : `${formatCost(totalLow)} – ${formatCost(totalHigh)}`

  const handleConfirm = () => {
    onConfirm(note)
    setNote('')
    onOpenChange(false)
  }

  const handleCancel = () => {
    setNote('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="submit-plan-modal">
        <DialogHeader>
          <DialogTitle>{t('submit_modal.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Plan statistics */}
          <div className="rounded-md border border-border bg-muted/40 p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('submit_modal.stat_worksites')}</span>
              <span className="font-medium text-foreground">{nonIgnored.length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('submit_modal.stat_envelope')}</span>
              <span className="font-medium text-foreground">{envelopeDisplay}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('submit_modal.stat_draft')}</span>
              <span className="font-medium text-foreground">{draftCount}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('submit_modal.stat_horizon')}</span>
              <span className="font-medium text-foreground">
                {plan.horizon_start}–{plan.horizon_end}
              </span>
            </div>
          </div>

          {/* Cover note */}
          <div className="space-y-1.5">
            <Label htmlFor="submit-cover-note">
              {t('submit_modal.cover_note_label')}
              <span className="ml-1 text-xs text-muted-foreground">
                ({t('submit_modal.optional')})
              </span>
            </Label>
            <Textarea
              id="submit-cover-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('submit_modal.cover_note_placeholder')}
              rows={3}
              data-testid="submit-cover-note"
            />
          </div>

          {/* Warning */}
          <div className="rounded-md border border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-950/30 p-3">
            <p className="text-sm text-yellow-800 dark:text-yellow-300">
              {t('submit_modal.readonly_warning')}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            {t('submit_modal.cancel')}
          </Button>
          <Button onClick={handleConfirm} data-testid="submit-plan-confirm">
            {t('submit_modal.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
